import React, { useContext } from 'react';
import type { FC } from 'react';

import type CustomerApi from '../../../services/customer/CustomerApi';
import type {
  PaymentMethod,
  PaymentMethodType,
} from '../../../services/commonCheckout/types/PaymentMethod';
import noop from '../../../utils/function/noop';
import {
  getAgent,
  getCardCategories,
} from '../../../utils/session/selectors';
import {
  getFullYearFromFormExpirationField,
  getMonthFromFormExpirationField,
  getShortCombinedExpirationDate,
} from '../utils/selectors';
import { AppContext } from '../../contextStore/AppContext';
import type {
  EditPaymentMethodCardFormData,
  EditPaymentMethodCardFormProps,
} from '../types';
import type { MedicationOption } from '../../../shared/types/CardCategories/MedicationOption';
import { getMedicationOptionsFromManufacturerCardTypeConfig } from '../../../utils/session/getMedicationOptionsFromManufacturerCardTypeConfig';
import type { CardCategoriesType } from '../../../shared/types/CardCategories/CardCategoriesType';
import createCardCategoriesArray from '../../../utils/cardCategories/createCardCategoriesArray';
import { isManufacturerCardCheck } from '../../../utils/cardCategories/isManufacturerCardCheck';
import { getMedicationValue } from '../../../utils/cardCategories/getMedicationValue';

import EditPaymentMethodCardForm from './EditPaymentMethodCardForm';

export type EditPaymentMethodCardContainerProps = {
  customerApi: typeof CustomerApi;
  paymentMethod: PaymentMethod<'CARD'>;
  onUpdateSuccess?: ({
    paymentMethodId,
    isDefault,
  }: {
    paymentMethodId: string;
    isDefault: boolean;
  }) => void;
  onUpdateFailure?: () => void;
  onBackClick?: () => void;
  onRemoveClick?: () => void;
  onCancelClick: () => void;
  showBackButton?: boolean;
};
const EditPaymentMethodCardContainer: FC<
  EditPaymentMethodCardContainerProps
> = ({
  paymentMethod,
  customerApi,
  onUpdateSuccess = noop,
  onUpdateFailure = noop,
  onBackClick = noop,
  onRemoveClick,
  onCancelClick,
  showBackButton = true,
}) => {
  const {
    initialSessionData,
    originalCheckoutSessionResponse,
    setData,
  } = useContext(AppContext);
  const medicationOptions: MedicationOption[] =
    getMedicationOptionsFromManufacturerCardTypeConfig(
      initialSessionData,
    );

  const cardCategoriesConfig = getCardCategories(
    initialSessionData,
  );

  const agent = getAgent(originalCheckoutSessionResponse);
  const medicationValue = getMedicationValue(paymentMethod);

  const updatePaymentMethod = async ({
    nameOnCard,
    expiration,
    zipCode,
    nickname,
    isDefault,
    isManufacturerCard,
    medication,
  }: EditPaymentMethodCardFormData) => {
    setData({ overlayLoaderConfig: { show: true } });

    let expiryMonth = 0;
    let expiryYear = 0;

    if (expiration) {
      expiryMonth = getMonthFromFormExpirationField(expiration);
      expiryYear =
        getFullYearFromFormExpirationField(expiration);
    }
    const paymentMethodCardDetails: {
      type: Extract<PaymentMethodType, 'CARD'>;
      nameOnCard: string;
      expiryYear: number;
      expiryMonth: number;
      zipCode: string;
      manufacturerCard: boolean;
      cardCategories: CardCategoriesType;
    } = {
      type: 'CARD',
      nameOnCard,
      expiryYear,
      expiryMonth,
      zipCode,
      manufacturerCard: isManufacturerCard,
      cardCategories: createCardCategoriesArray({
        manufacturerCardTypeOptions: {
          value: medication,
          medications: medicationOptions,
        },
      }),
    };

    const result = await customerApi
      .updatePaymentMethod(
        {
          customerId: paymentMethod.customerId,
          paymentMethodId: paymentMethod.id,
        },
        {
          isDefault,
          nickname,
          agent,
          paymentMethodDetails: paymentMethodCardDetails,
        },
      )
      .finally(() => {
        setData({ overlayLoaderConfig: { show: false } });
      });

    if (!result?.errors || result.errors?.length === 0) {
      onUpdateSuccess({
        paymentMethodId: paymentMethod.id,
        isDefault,
      });
    } else {
      onUpdateFailure();
    }
  };

  const handleOnSubmit: EditPaymentMethodCardFormProps['onSubmit'] =
    (formData) => {
      updatePaymentMethod(formData).catch((error: unknown) => {
        console.error(
          'Error while updating payment method: ',
          error,
        );
      });
    };

  return (
    <EditPaymentMethodCardForm
      expiration={getShortCombinedExpirationDate(
        paymentMethod.paymentMethodDetails.expiryMonth,
        paymentMethod.paymentMethodDetails.expiryYear,
      )}
      lastFour={paymentMethod.paymentMethodDetails.last4}
      nameOnCard={paymentMethod.paymentMethodDetails.nameOnCard}
      zipCode={paymentMethod.zipCode}
      nickname={paymentMethod.nickname}
      isDefault={paymentMethod.default}
      isManufacturerCard={isManufacturerCardCheck(paymentMethod)}
      onSubmit={handleOnSubmit}
      onBackClick={onBackClick}
      onRemoveClick={onRemoveClick}
      onCancelClick={onCancelClick}
      showBackButton={showBackButton}
      medicationOptions={medicationOptions}
      cardCategoriesConfigObject={cardCategoriesConfig}
      medication={medicationValue}
    />
  );
};

export default EditPaymentMethodCardContainer;
